import React, { useContext } from 'react';
import styled from 'styled-components';
import { Image, Type } from '@spotify-internal/encore-creator-web';
import { spotifyGreenHighlight } from '@spotify-internal/encore-foundation';
import { Show } from '../../../types';
import { PodcastModalBody } from '../../Shared/PodcastModal';
import { AddPodcastModalContext } from '../AddPodcastModal/context';
import { FormattedMessage } from 'react-intl';

interface Props {
  selectedShowId: string | null;
  shows: Show[];
  handlePodcastSelection(show: Show): void;
  handleNewSubmission: () => void;
}

export function Picker(props: Props) {
  const [selectedPodcastId, setSelectedPodcastId] = React.useState<string | undefined>(undefined);
  const { setPreviewPanePodcast, setNextButtonDisabled } = useContext(AddPodcastModalContext);

  React.useEffect(() => {
    setNextButtonDisabled(!props.selectedShowId);

    if (props.shows.length === 1) {
      handlePodcastClick(props.shows[0]);
    } else if (props.selectedShowId) {
      setSelectedPodcastId(props.selectedShowId);
    }
  }, []);

  const handleNewSubmission = () => {
    props.handleNewSubmission();
    setNextButtonDisabled(false);
  };

  /* istanbul ignore next */
  function handlePodcastClick(show: Show) {
    setSelectedPodcastId(show.id);
    props.handlePodcastSelection(show);

    setPreviewPanePodcast({
      title: show.name,
      image: show.artworkUrl,
      description: show.description,
      author: show.publisher,
    });
    setNextButtonDisabled(false);
  }

  const hasMultipleMatches = props.shows.length > 1;

  function renderMutlipleMatches() {
    return (
      <>
        <Type.h1 variant={Type.heading2} data-testid="select-podcast-header">
          <FormattedMessage
            id="add-podcast.picker-title"
            defaultMessage="Which podcast is yours?"
          />
        </Type.h1>

        <Type.p variant={Type.body1}>
          <FormattedMessage
            id="add-podcast.picker-instructions"
            defaultMessage="Click on the cover that matches your podcast to continue."
          />
        </Type.p>

        <PickerGrid>
          {props.shows.map(show => (
            <PickerItem
              data-testid={`select-podcast-${show.id}`}
              key={show.id}
              onClick={/* istanbul ignore next */ () => handlePodcastClick(show)}
              opacity={
                /* istanbul ignore next */ selectedPodcastId && selectedPodcastId != show.id
                  ? 0.3
                  : 1
              }
            >
              <PodcastCover src={show.artworkUrl} alt={show.name} />
              <Type.p variant={Type.body2} style={{ width: '130px', marginTop: '12px' }}>
                <FormattedMessage
                  id="add-podcast.picker-by"
                  defaultMessage="By {publisher}"
                  description="Who is the author of this podcast?"
                  values={{
                    publisher: show.publisher,
                  }}
                />
              </Type.p>
            </PickerItem>
          ))}
        </PickerGrid>
        <Type.p variant={Type.body1}>
          <ButtonLink onClick={handleNewSubmission}>
            <FormattedMessage
              id="add-podcast.picker-none-are-mine"
              defaultMessage="None of these podcasts are mine"
            />
          </ButtonLink>
        </Type.p>
      </>
    );
  }

  function renderSingleMatch() {
    const show = props.shows[0];
    return (
      <>
        <Type.h1 variant={Type.heading2} data-testid="select-podcast-header">
          <FormattedMessage id="add-podcast.picker-single" defaultMessage="Is this your podcast?" />
        </Type.h1>

        <PickerItem data-testid={`select-podcast-${show.id}`}>
          <PodcastCover src={show.artworkUrl} alt={show.name} />
          <Type.p variant={Type.body2} style={{ width: '130px', marginTop: '12px' }}>
            <FormattedMessage
              id="add-podcast.picker-by-2"
              defaultMessage="By {publisher}"
              description="Who is the author of this podcast?"
              values={{
                publisher: show.publisher,
              }}
            />
          </Type.p>
        </PickerItem>

        <div>
          <Type.p variant={Type.body1}>
            <FormattedMessage
              id="add-podcast.picker-by-2"
              defaultMessage="If so, hit <strong>next</strong> to claim it."
              values={{
                strong: (...chunks) => <strong>{chunks}</strong>,
              }}
            />
          </Type.p>
          <Type.p variant={Type.body1}>
            <FormattedMessage
              id="add-podcast.picker-by-2"
              defaultMessage="If not, you can still <link>submit your podcast to Spotify.</link>"
              values={{
                link: (...chunks) => (
                  <ButtonLink onClick={handleNewSubmission} data-testid="podcast-picker--opt-out">
                    {chunks}
                  </ButtonLink>
                ),
              }}
            />
          </Type.p>
        </div>
      </>
    );
  }

  return (
    <PodcastModalBody>
      {hasMultipleMatches ? renderMutlipleMatches() : renderSingleMatch()}
    </PodcastModalBody>
  );
}

const PickerGrid = styled.div`
  display: grid;
  grid-template-columns: 130px 130px 130px;
  grid-column-gap: 36px;
  grid-row-gap: 36px;
  margin-top: 32px;
`;

const PickerItem = styled.div<{ opacity?: number }>`
  display: block;
  position: relative;
  opacity: ${props => props.opacity};
`;

const PodcastCover = styled(Image).attrs<{
  src: string;
  name: string;
}>(props => ({
  src: props.src,
  alt: props.name,
  imageHeight: '130px',
  imageWidth: '130px',
  crop: true,
}))`
  box-shadow: #d8d8d8 0 3px 6px 1px;
`;

const ButtonLink = styled.span`
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${spotifyGreenHighlight};
  }
`;
